import { BlurImage, ProductGroup } from "../types/firestore/productGroup.js"

type ImageValue = BlurImage | string | null | undefined

interface ItemImageSource {
  image?: ImageValue
  images?: readonly ImageValue[]
}

function normalizeImage(image: ImageValue): BlurImage | undefined {
  if (typeof image === "string") {
    return image && image !== "none" ? { url: image } : undefined
  }

  return image?.url && image.url !== "none" ? image : undefined
}

/**
 * Returns an item's ordered gallery. The legacy `image` field remains the fallback
 * for records that do not have the `images` field yet.
 */
export function getItemImages(item?: ItemImageSource): BlurImage[] {
  const sourceImages = item?.images !== undefined ? item.images : [item?.image]
  const seenUrls = new Set<string>()
  const images: BlurImage[] = []

  for (const image of sourceImages) {
    const normalizedImage = normalizeImage(image)
    if (!normalizedImage || seenUrls.has(normalizedImage.url)) {
      continue
    }

    seenUrls.add(normalizedImage.url)
    images.push(normalizedImage)
  }

  return images
}

export function getPrimaryItemImage(item?: ItemImageSource): BlurImage | undefined {
  return getItemImages(item)[0]
}

/** Resolve the gallery for a storefront product and its selected item. */
export function getProductGroupImages(productGroup: ProductGroup, itemId?: string): BlurImage[] {
  const items = productGroup.items || []
  const visibleItems = items.filter(item => item.visible)
  const imageItemId = itemId || (items.length === 1 && visibleItems.length === 1 ? visibleItems[0].id : undefined)

  if (imageItemId) {
    const selectedItemImages = getItemImages(visibleItems.find(item => item.id === imageItemId))
    if (selectedItemImages.length) {
      return selectedItemImages
    }
  }

  if (productGroup.image?.url) {
    return [productGroup.image]
  }

  for (const item of [...visibleItems, ...items]) {
    const itemImages = getItemImages(item)
    if (itemImages.length) {
      return itemImages
    }
  }

  return []
}
